import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { PROJECT_CATEGORIES, PROJECTS } from "../../data/content";
import { ZoomIn } from "lucide-react";

export default function ProjectShowcase({ limit }) {
  const [cat, setCat] = useState("all");
  const filtered = PROJECTS.filter((p) => cat === "all" || p.cat === cat);
  const shown = limit ? filtered.slice(0, limit) : filtered;

  return (
    <section
      data-testid="project-showcase"
      className="py-20 md:py-32 bg-white border-t border-ink-line"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink-muted mb-5">
              Project Showcase
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-ink leading-tight tracking-[-0.03em]">
              Selected work.
            </h2>
          </div>
          {limit && (
            <Link
              to="/projects"
              data-testid="projects-view-all"
              className="text-[11px] tracking-[0.25em] uppercase text-ink hover:text-ink-soft border-b border-ink pb-1 self-start md:self-end"
            >
              View all projects →
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-wrap gap-2">
          {PROJECT_CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`projects-filter-${c.key}`}
              onClick={() => setCat(c.key)}
              className={`text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors ${
                cat === c.key
                  ? "bg-ink text-white border-ink"
                  : "bg-white text-ink-muted border-ink-line hover:border-ink hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {shown.map((p) => (
            <Dialog key={p.id}>
              <DialogTrigger asChild>
                <button
                  data-testid={`project-${p.id}`}
                  className="group relative aspect-[4/5] overflow-hidden bg-ink block"
                >
                <img
                  src={p.image}
                  alt={`${p.sub} project — Urban Painter`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                  <div className="absolute inset-0 p-4 flex flex-col justify-between text-white">
                    <div className="flex justify-between items-start">
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase bg-white/10 backdrop-blur px-2 py-1">
                        {PROJECT_CATEGORIES.find((c) => c.key === p.cat)?.label}
                      </span>
                      <span className="w-8 h-8 grid place-items-center bg-white/0 group-hover:bg-white group-hover:text-ink transition-colors">
                        <ZoomIn size={14} />
                      </span>
                    </div>
                    <div className="font-display text-lg md:text-xl">{p.sub}</div>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl p-0 bg-transparent border-0 rounded-none">
                <DialogTitle className="sr-only">
                  {p.sub} — {p.cat}
                </DialogTitle>
                <img
                  src={p.image}
                  alt={`${p.sub} project`}
                  className="w-full h-auto"
                />
              </DialogContent>
            </Dialog>
          ))}
        </div>

        <p className="mt-8 text-xs text-ink-muted italic max-w-2xl">
          All epoxy flooring and waterproofing photographs shown here are real
          Urban Painter projects. Painting images are indicative — real project
          photographs will be published as they become available.
        </p>
      </div>
    </section>
  );
}
